import { useMemo, useState, type ReactNode } from 'react'
import { useParams } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  DEFINITION_CHOICE_OPTIONS,
  LANGUAGE_CHOICE_OPTIONS,
  type LanguageChoice,
  type LanguageItem,
} from '@cf/shared'
import { toast } from 'sonner'
import { AppShell, PageBody, initialsOf } from '@/components/AppShell'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Choice } from '@/components/ui/choice'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { languageApi } from '@/lib/api'

type RowAnswer = {
  choice: string | null
  goodChoice: string | null
  note: string | null
}

/**
 * The language sheet: rows of "wrong -> right" with a proposed priority.
 *
 * The proposal is shown as the ghost answer; a click makes it (or a different
 * choice) explicit and saves at once. A reviewer who agrees with everything
 * can legitimately send the sheet untouched — the export records which rows
 * were confirmed by hand and which were left standing.
 */
export function LanguagePage() {
  const { token } = useParams({ from: '/l/$token' })
  const client = useQueryClient()
  const query = useQuery({
    queryKey: ['language', token],
    queryFn: () => languageApi.payload(token),
  })
  const [local, setLocal] = useState<Record<string, RowAnswer>>({})
  const [general, setGeneral] = useState<string | null>(null)
  const [sent, setSent] = useState(false)

  const save = useMutation({
    mutationFn: ({ itemId, answer }: { itemId: string; answer: RowAnswer }) =>
      languageApi.save(token, itemId, {
        choice: (answer.choice as LanguageChoice | null) ?? null,
        goodChoice: (answer.goodChoice as LanguageChoice | null) ?? null,
        note: answer.note,
      }),
    onError: (error) =>
      toast.error('Nie udało się zapisać', {
        description: error instanceof Error ? error.message : undefined,
      }),
  })

  const submit = useMutation({
    mutationFn: () =>
      languageApi.submit(token, general ?? query.data?.general ?? null),
    onSuccess: () => {
      setSent(true)
      void client.invalidateQueries({ queryKey: ['language', token] })
    },
    onError: (error) =>
      toast.error('Nie udało się wysłać', {
        description: error instanceof Error ? error.message : undefined,
      }),
  })

  const answers = useMemo(() => {
    const merged: Record<string, RowAnswer> = { ...(query.data?.answers ?? {}) }
    for (const [id, answer] of Object.entries(local)) merged[id] = answer
    return merged
  }, [query.data?.answers, local])

  if (query.isLoading || !query.data) {
    return (
      <AppShell>
        <PageBody className="flex max-w-[860px] flex-col gap-4">
          <Skeleton className="h-64 w-full rounded-xl" />
        </PageBody>
      </AppShell>
    )
  }

  const { sheet, nick, displayName } = query.data
  const readOnly = Boolean(query.data.submittedAt) || sent
  const explicitCount = Object.values(answers).filter(
    (a) => a.choice || a.goodChoice,
  ).length

  const update = (itemId: string, next: RowAnswer) => {
    setLocal((prev) => ({ ...prev, [itemId]: next }))
    save.mutate({ itemId, answer: next })
  }

  return (
    <AppShell
      subtitle={sheet.title}
      identity={{ initials: initialsOf(displayName ?? nick), name: displayName ?? nick }}
      right={
        <span className="whitespace-nowrap text-[13px] text-muted-foreground">
          ocenione {explicitCount} / {query.data.totalItems}
        </span>
      }
    >
      <PageBody className="flex max-w-[860px] flex-col gap-5 pb-16">
        {readOnly ? (
          <Alert>
            <AlertTitle>Arkusz wysłany — dziękujemy!</AlertTitle>
            <AlertDescription>
              Odpowiedzi są zamknięte do odczytu. Jeśli chcesz coś poprawić, napisz
              do nas.
            </AlertDescription>
          </Alert>
        ) : null}

        {sheet.sections.map((section) => (
          <section key={section.id} className="flex flex-col gap-3">
            <div>
              <h2 className="font-serif text-[18px]">{section.title}</h2>
              {section.description ? (
                <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
                  {section.description}
                </p>
              ) : null}
            </div>
            {section.items.map((item) =>
              sheet.kind === 'definitions' ? (
                <DefinitionRow
                  key={item.id}
                  item={item}
                  answer={
                    answers[item.id] ?? { choice: null, goodChoice: null, note: null }
                  }
                  readOnly={readOnly}
                  onChange={(next) => update(item.id, next)}
                />
              ) : (
                <LanguageRow
                  key={item.id}
                  item={item}
                  answer={
                    answers[item.id] ?? { choice: null, goodChoice: null, note: null }
                  }
                  readOnly={readOnly}
                  onChange={(next) => update(item.id, next)}
                />
              ),
            )}
          </section>
        ))}

        <Card>
          <CardHeader>
            <CardTitle className="text-[16px]">Uwagi ogólne i wysyłka</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <Textarea
              value={general ?? query.data.general ?? ''}
              onChange={(event) => setGeneral(event.target.value)}
              disabled={readOnly}
              rows={4}
            />
            <div className="flex justify-end">
              <Button
                onClick={() => submit.mutate()}
                disabled={readOnly || submit.isPending}
              >
                Wyślij arkusz
              </Button>
            </div>
          </CardContent>
        </Card>
      </PageBody>
    </AppShell>
  )
}

/**
 * A dictionary-definition row: the term, its wording, and one question — how
 * common is this term at the board. No pre-selection: frequency is exactly
 * the thing we do not want to suggest.
 */
function DefinitionRow({
  item,
  answer,
  readOnly,
  onChange,
}: {
  item: LanguageItem
  answer: RowAnswer
  readOnly: boolean
  onChange: (next: RowAnswer) => void
}) {
  const [note, setNote] = useState(answer.note ?? '')
  const explicit = Boolean(answer.choice)
  return (
    <Card className={explicit ? 'border-primary/40' : undefined}>
      <CardContent className="flex flex-col gap-2.5 pt-3">
        <div className="flex items-baseline justify-between gap-2">
          <strong className="text-[15px] font-medium">{item.term}</strong>
          <Badge variant="outline" className="font-mono">
            {item.id}
          </Badge>
        </div>
        <p className="text-[13px] leading-relaxed text-muted-foreground">
          {item.definition}
        </p>
        <Choice
          options={DEFINITION_CHOICE_OPTIONS}
          value={answer.choice}
          onChange={(value) => {
            if (readOnly) return
            onChange({ ...answer, choice: value })
          }}
          ariaLabel={`Częstość terminu ${item.id}`}
          className="max-w-[420px]"
        />
        <Input
          value={note}
          onChange={(event) => setNote(event.target.value)}
          onBlur={() => {
            const trimmed = note.trim() || null
            if (trimmed !== (answer.note ?? null)) {
              onChange({ ...answer, note: trimmed })
            }
          }}
          disabled={readOnly}
          placeholder="uwaga do terminu albo definicji (opcjonalnie)"
          className="mt-1.5 h-8 text-[13px]"
        />
      </CardContent>
    </Card>
  )
}

/** The subtle provenance tag: same on both phrases, so neither one shouts. */
function PhraseLabel({ children }: { children: ReactNode }) {
  return (
    <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
      {children}
    </span>
  )
}

function LanguageRow({
  item,
  answer,
  readOnly,
  onChange,
}: {
  item: LanguageItem
  answer: RowAnswer
  readOnly: boolean
  onChange: (next: RowAnswer) => void
}) {
  // The note types locally and saves on blur; a request per keystroke would
  // hammer the API for no benefit. Choices save on click — one click, one row.
  const [note, setNote] = useState(answer.note ?? '')
  const effective = answer.choice ?? item.proposed
  const explicit = Boolean(answer.choice)
  // Our own replacement obviously carries our vote of confidence, so its
  // ghost answer is "tak się mówi" until the reviewer says otherwise.
  const effectiveGood = answer.goodChoice ?? 'OK'
  const explicitGood = Boolean(answer.goodChoice)
  return (
    <Card className={explicit || explicitGood ? 'border-primary/40' : undefined}>
      <CardContent className="flex flex-col gap-2.5 pt-3">
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between gap-2">
            <PhraseLabel>model</PhraseLabel>
            <Badge variant="outline" className="font-mono">
              {item.id}
            </Badge>
          </div>
          <strong className="text-[15px] font-medium">„{item.bad}”</strong>
          {item.example ? (
            <p className="text-[13px] leading-relaxed text-muted-foreground">
              w komentarzu: <em>„{item.example}”</em>
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Choice
            options={LANGUAGE_CHOICE_OPTIONS}
            value={effective}
            onChange={(value) => {
              if (readOnly) return
              onChange({ ...answer, choice: value })
            }}
            ariaLabel={`Ocena zwrotu ${item.id}`}
            className="max-w-[520px]"
          />
        </div>
        {item.rateGood ? (
          <div className="flex flex-col gap-1.5 border-t pt-2.5">
            <PhraseLabel>sugestia</PhraseLabel>
            <strong className="text-[15px] font-medium">„{item.good}”</strong>
            <div className="flex flex-wrap items-center gap-2">
              <Choice
                options={LANGUAGE_CHOICE_OPTIONS}
                value={effectiveGood}
                onChange={(value) => {
                  if (readOnly) return
                  onChange({ ...answer, goodChoice: value })
                }}
                ariaLabel={`Ocena zamiany ${item.id}`}
                className="max-w-[520px]"
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-1 border-t pt-2.5">
            <PhraseLabel>wskazówka</PhraseLabel>
            <p className="text-[13px] text-muted-foreground">„{item.good}”</p>
          </div>
        )}
        <Input
          value={note}
          onChange={(event) => setNote(event.target.value)}
          onBlur={() => {
            const trimmed = note.trim() || null
            if (trimmed !== (answer.note ?? null)) {
              onChange({ ...answer, note: trimmed })
            }
          }}
          disabled={readOnly}
          placeholder="Twoja wersja albo uwaga (opcjonalnie)"
          className="mt-1.5 h-8 text-[13px]"
        />
      </CardContent>
    </Card>
  )
}
